import { RadioSlider, RadioSliderProps } from '@almbrand/radioslider';
import { ThemeUnion } from '@almbrand/systemicons/dist/cjs/types/icon';
import classNames from 'classnames';
import { PageContext } from 'context/PageContext';
import { useContext, useEffect } from 'react';
import { FieldValues, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { removeRadioState, setCurrentRadioState } from 'store/slices/radioCardSlice';
import { store } from 'store/store';
import styles from './RadioSliderComponent.module.scss';

export type RadioSliderComponentProps = RadioSliderProps<ThemeUnion> & {
	register: UseFormRegister<FieldValues>;
	contentType: string;
	submitStep?: () => void;
	setValue?: UseFormSetValue<FieldValues>;
	trigger?: UseFormTrigger<FieldValues>;
	stepTitle?: string;
};

export const RadioSliderComponent: React.FC<RadioSliderComponentProps> = ({
	register,
	submitStep,
	name,
	contentType,
	errorMessage,
	hasError,
	setValue,
	trigger,
	...props
}) => {
	const pageContext = useContext(PageContext);
	const { theme } = pageContext ?? {};

	const { onChange, name: RadioSliderName } = register(name, { required: true });
	const currentRadioState = useAppSelector((state) => state.currentRadioState);

	const dispatch = useAppDispatch();

	// map values
	const values = [];
	props.values.forEach((element) => {
		values.push({ [element.key]: element.value });
	});

	const getSelectedIndex = () => {
		const selectedElement = currentRadioState.find((state) => state.name === name);
		if (selectedElement) {
			return selectedElement.index;
		}
	};

	const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.currentTarget;

		const selected = values.find((v) => v[Object.keys(v)[0]] === value);
		let index = -1;
		values.forEach((v, inx) => {
			if (v[Object.keys(v)[0]] === value) {
				index = inx;
			}
		});
		dispatch(setCurrentRadioState({ index, name }));
		void onChange({
			target: {
				name: RadioSliderName,
				value: {
					id: name,
					value,
					index,
					label: Object.keys(selected)[0],
					type: contentType,
					stepTitle: props.stepTitle,
				},
			},
		});
		void trigger(name);
		submitStep?.();
	};

	useEffect(() => {
		const value = store.getState().calculatorForm[name]?.data?.value;
		if (value) {
			setValue(name, {
				id: name,
				value,
				type: contentType,
				stepTitle: props.stepTitle,
			});
		} else {
			dispatch(removeRadioState(name));
		}
	}, []);

	return (
		<RadioSlider
			{...props}
			className={classNames(styles.RadioSliderComponent)}
			values={values}
			onChange={handleOnChange}
			selectedIndex={getSelectedIndex()}
			themeName={theme}
			{...(hasError ? { hasError: true, errorMessage } : { hasError: false })}
		/>
	);
};
